// src/utils/summary.js
import { format, parseISO, isValid } from 'date-fns';

/** Unidades de origen por métrica (ajustá si agregás nuevas) */
export const METRIC_UNIT = {
  device_activity: 'seconds',
  mopping_time: 'seconds',
  avg_mopping_time: 'seconds',
  water_changes_count: 'count', // no es tiempo
};

/* -------------------- Normalización numérica (para charts) -------------------- */

/** Segundos -> HORAS (number) */
export const toHours = (sec) => Number(sec || 0) / 3600;

/**
 * HORAS (number) -> "hh:mm" o "hh:mm:ss"
 * - showSeconds: true para tooltip (más precisión), false para eje (más limpio)
 */
export const hoursToHHMM = (hours, { showSeconds = false } = {}) => {
  const hFloat = Math.max(0, Number(hours) || 0);
  let h = Math.floor(hFloat);
  let m = Math.floor((hFloat - h) * 60);
  let s = Math.round(((hFloat - h) * 60 - m) * 60);

  // Normalización (por si el redondeo empuja a 60)
  if (s >= 60) {
    s -= 60;
    m += 1;
  }
  if (m >= 60) {
    m -= 60;
    h += 1;
  }

  const HH = String(h).padStart(2, '0');
  const MM = String(m).padStart(2, '0');

  if (!showSeconds) return `${HH}:${MM}`;

  const SS = String(s).padStart(2, '0');

  return `${HH}:${MM}:${SS}`;
};

/**
 * Normaliza un valor al número que debe consumir el chart.
 * - Métricas en segundos -> horas (number)
 * - Métricas de conteo -> number
 */
export const normalizeValue = (metricKey, v) =>
  METRIC_UNIT[metricKey] === 'seconds' ? toHours(v) : Number(v || 0);

/** Sufijo recomendado para el eje Y del chart */
export const suffixFor = (metricKey) => (METRIC_UNIT[metricKey] === 'count' ? '' : 'h');

/* -------------------------- Helpers de formateo UI --------------------------- */

/** Segundos -> "hh:mm:ss" (robusto a comas y normaliza 60s/60m). Útil si necesitás segundos crudos en otra UI. */
export const secondsToHHMMSS = (sec, { verbose = false, showSeconds = true } = {}) => {
  // 1) Parseo robusto (coma o punto)
  const parse = (v) => {
    if (typeof v === 'string') v = v.trim().replace(',', '.');
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  let total = Math.max(0, parse(sec)); // en segundos

  // 2) Descomposición
  let h = Math.floor(total / 3600);
  let m = Math.floor((total % 3600) / 60);
  let s = total - h * 3600 - m * 60; // puede tener decimales

  // 3) Redondeo a segundo entero
  s = Math.round(s);

  // 4) Normalización por si s o m se pasan
  if (s >= 60) {
    s -= 60;
    m += 1;
  }
  if (m >= 60) {
    m -= 60;
    h += 1;
  }

  // 5) Si no mostramos segundos, redondeamos al minuto más cercano
  if (!showSeconds) {
    if (s >= 30) m += 1; // redondeo
    s = 0; // ocultamos segundos

    // Normalización de minutos por si sumó
    if (m >= 60) {
      m -= 60;
      h += 1;
    }
  }

  const HH = String(h).padStart(2, '0');
  const MM = String(m).padStart(2, '0');
  const SS = String(s).padStart(2, '0');

  if (verbose) {
    return showSeconds ? `${HH}h ${MM}m ${SS}s` : `${HH}h ${MM}m`;
  }

  return showSeconds ? `${HH}h ${MM}m ${SS}s` : `${HH}:${MM}`;
};

/**
 * Para UI (cards/tooltip): si la métrica es tiempo (seconds de origen, pero ya normalizado a HORAS),
 * formatea en "hh:mm:ss" (o "hh:mm" si preferís menos ruido). Si es conteo, devuelve número.
 */
export const formatValueLabel = (metricKey, v, opts) =>
  METRIC_UNIT[metricKey] === 'seconds'
    ? hoursToHHMM(v, { showSeconds: true, ...(opts || {}) }) // v ya está en HORAS por normalizeValue
    : String(Number(v || 0));

/* ----------------------------- Builders de series ---------------------------- */

const safeParse = (iso) => {
  const d = parseISO(String(iso || ''));
  return isValid(d) ? d : new Date(NaN); // NaN -> evitar explotar en format
};

/** Semanas -> { id, label: "WEEK N MMM d – d", value } */
export const formatToWeeks = (data = [], metricKey) =>
  data.map((item, index) => {
    const start = safeParse(item.startDate);
    const end = safeParse(item.endDate);
    const startLabel = isValid(start) ? format(start, 'MMM d') : '—';
    const endLabel = isValid(end) ? format(end, 'd') : '—';

    return {
      id: index,
      label: `WEEK ${index + 1} ${startLabel} – ${endLabel}`,
      value: normalizeValue(metricKey, item.value), // horas o count
    };
  });

/** Días -> { id, dateISO, label: "MMM d" (UPPER), value } */
export const buildDayTicks = (days = [], metricKey) =>
  days.map((d, i) => {
    const dt = safeParse(d.startDate);
    const label = isValid(dt) ? format(dt, 'MMM d').toUpperCase() : '—';
    return {
      id: i,
      dateISO: d.startDate,
      label,
      value: normalizeValue(metricKey, d.value), // horas o count
    };
  });

/** Meses (ordenados) -> { id, monthStartISO, label: "MMMM yyyy" (UPPER), value } */
export const buildMonthTicks = (months = [], metricKey) => {
  const sorted = [...months].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

  return sorted.map((m, i) => {
    const start = safeParse(m.startDate);
    const label = isValid(start) ? format(start, 'MMMM yyyy').toUpperCase() : '—';
    return {
      id: i,
      monthStartISO: m.startDate,
      label,
      value: normalizeValue(metricKey, m.value), // horas o count
    };
  });
};
